import { Wallet, Target, GitBranch, Search, AlertCircle, Gavel } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, Slab, MockVisual, CTABanner } from '@/components/marketing/primitives.jsx';

const capabilities = [
  {
    icon: Target,
    title: 'Federal Fund Objectives',
    description:
      'Each program declares authoritative objectives — what the money is for — with structured criteria that downstream gates can evaluate against.',
  },
  {
    icon: GitBranch,
    title: 'Gate lifecycle',
    description:
      'Evaluations combine deterministic checks with probabilistic signals. Each gate decision is signed by its reviewer and linked to the FFO.',
  },
  {
    icon: Search,
    title: 'Anomaly detection',
    description:
      'Pattern anomalies surface on the FIRL dashboard for program-officer triage. Nothing auto-acts on funds; humans approve enforcement.',
  },
  {
    icon: AlertCircle,
    title: 'Enforcement board',
    description:
      'Approved enforcement actions — hold, pause, claw-back — are recorded with full rationale and made accessible to the Inspector General.',
  },
  {
    icon: Wallet,
    title: 'Transaction audit',
    description:
      'Every evaluation, anomaly flag, and enforcement action is part of the append-only audit and exportable as a signed WORM bundle.',
  },
  {
    icon: Gavel,
    title: 'Judicial integration',
    description:
      'Actions requiring a warrant are packaged through the Judicial layer with all evidence pre-attached and signed for reviewer clarity.',
  },
];

export default function FIRLPage() {
  return (
    <>
      <Hero
        eyebrow="FIRL — Funds Integrity Routing Layer"
        title="Federal fund flow with objectives you can prove."
        description="FFO declaration, gate evaluation, anomaly surfacing, reviewer-signed enforcement — all with end-to-end audit and judicial integration for the hard cases."
        primaryCta={{ to: '/contact', label: 'Talk to program officers' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Capabilities"
            title="From objective declaration to signed enforcement."
          />
          <div className="mt-12">
            <FeatureGrid items={capabilities} columns={3} />
          </div>
        </div>
      </section>

      <Slab
        eyebrow="FFO lifecycle"
        title="Declared, evaluated, enforced — nothing implicit."
        description="A Federal Fund Objective is a first-class record with versioning, attached criteria, and a lifecycle state. Gates execute against the FFO version in effect at evaluation time — no retroactive rule changes."
        bullets={[
          'Immutable FFO versions with explicit supersedes_id chain',
          'Deterministic checks ride alongside probabilistic signals',
          'Every gate outcome is signed by the reviewer that approved it',
        ]}
        image={<MockVisual accent="emerald" lines={5} />}
      />

      <Slab
        reverse
        eyebrow="Anomaly detection"
        title="Signals surface. Humans decide."
        description="The anomaly board surfaces clusters, outliers, and pattern shifts. FIRL never pauses a flow automatically — a program officer reviews and decides, with every action captured for the Inspector General."
        bullets={[
          'Pattern detectors run on the Hub, not on the money path',
          'All program-officer decisions are signed and logged',
          'Inspector General has standing read access across the chain',
        ]}
        image={<MockVisual accent="amber" lines={6} />}
      />

      <CTABanner
        title="Bring your program onto FIRL."
        description="Tiered onboarding from observer to fully-federated enforcement authority. All integrations run through the Interoperability Sandbox."
        primaryCta={{ to: '/for-partners', label: 'Partnership tiers' }}
        secondaryCta={{ to: '/contact', label: 'Contact our team' }}
      />
    </>
  );
}
